import React from 'react';
import { Alert, Table } from 'react-bootstrap';

/** Integer minor units come from the server; never reallocate in the browser. */
export interface OpenerSalesRow {
  id: number | null;
  name: string;
  net_minor: number;
  receipt_count: number;
}

export interface OpenerSalesReport {
  version: 1;
  basis: 'collected_net_excluding_tips';
  attributed_net_minor: number;
  legacy_net_minor: number;
  scope_net_minor: number;
  waiters: Record<string, OpenerSalesRow>;
  salons: Record<string, OpenerSalesRow>;
  without_shift?: OpenerSalesReport;
}

// The host application is Spanish-only and uses React Bootstrap. Reuse its
// existing primitives rather than adding a parallel component/token system.
export const attributionCopy = {
  title: 'Ventas por apertura original',
  explanation: 'Cobrado neto sin propinas. Cada dimensión suma el mismo total; no se suman entre sí.',
  waiter: 'Mesero de apertura',
  salon: 'Salón de apertura',
  net: 'Venta neta',
  receipts: 'Recibos asociados',
  attributed: 'Total con atribución prospectiva',
  legacy: 'Total anterior sin atribución prospectiva',
  scope: 'Total neto de la sesión o turno',
  empty: 'Sin ventas con atribución prospectiva en este periodo.',
  invalid: 'No se pudo verificar la atribución de ventas.',
  legacyUsers: 'Registro de cobro por usuario (criterio anterior, incluye propinas)',
};

export const formatAttributionMinor = (value: number): string => {
  let currency = 'CLP';
  try {
    const configured = JSON.parse(localStorage.getItem('localConfiguration') ?? '{}').moneda_code;
    if (typeof configured === 'string' && /^[A-Z]{3}$/.test(configured)) currency = configured;
  } catch {
    // Match the existing application's default currency for absent old config.
  }
  return new Intl.NumberFormat('es-CL', { style: 'currency', currency, currencyDisplay: 'code',
    minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value / 100);
};

export function isReconciledReport(report: OpenerSalesReport): boolean {
  if (!report || typeof report !== 'object' || report.version !== 1
      || report.basis !== 'collected_net_excluding_tips'
      || ![report.attributed_net_minor, report.legacy_net_minor, report.scope_net_minor]
        .every(Number.isSafeInteger)) return false;
  if (report.attributed_net_minor + report.legacy_net_minor !== report.scope_net_minor) return false;
  return [report.waiters, report.salons].every(dimension =>
    dimension && typeof dimension === 'object' && !Array.isArray(dimension)
    && Object.values(dimension).every(row =>
      row && typeof row === 'object' && typeof row.name === 'string' && Number.isSafeInteger(row.net_minor)
      && Number.isSafeInteger(row.receipt_count) && row.receipt_count >= 0)
    && Object.values(dimension).reduce((sum, row) => sum + row.net_minor, 0)
      === report.attributed_net_minor);
}

/** Mounted only when the server returns the opt-in or persisted dimension. */
export default function OpenerSalesSection({ report }: { report: OpenerSalesReport }) {
  if (!isReconciledReport(report)) {
    return <Alert variant="danger" role="alert">{attributionCopy.invalid}</Alert>;
  }
  return (
    <section className="sb-section" aria-label={attributionCopy.title}>
      <h4 className="sb-section-title">{attributionCopy.title}</h4>
      <p>{attributionCopy.explanation}</p>
      <dl>
        <dt>{attributionCopy.attributed}</dt><dd>{formatAttributionMinor(report.attributed_net_minor)}</dd>
        <dt>{attributionCopy.legacy}</dt><dd>{formatAttributionMinor(report.legacy_net_minor)}</dd>
        <dt>{attributionCopy.scope}</dt><dd>{formatAttributionMinor(report.scope_net_minor)}</dd>
      </dl>
      {Object.keys(report.waiters).length === 0 ? <p>{attributionCopy.empty}</p> :
        ([['waiters', attributionCopy.waiter], ['salons', attributionCopy.salon]] as const).map(([key, label]) => (
          <Table key={key} responsive size="sm" aria-label={label}>
            <thead><tr><th scope="col">{label}</th><th scope="col">{attributionCopy.net}</th>
              <th scope="col">{attributionCopy.receipts}</th></tr></thead>
            <tbody>{Object.entries(report[key]).map(([id, row]) =>
              <tr key={id}><th scope="row">{row.name}</th>
                <td>{formatAttributionMinor(row.net_minor)}</td><td>{row.receipt_count}</td></tr>)}</tbody>
          </Table>
        ))}
    </section>
  );
}
